/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState, useMemo } from "react";
import { MarkerF as Marker } from "@react-google-maps/api";
import { ThreadType } from "types/ThreadType";
import { Point } from "./Point.type";
import ImageFromStrapiMedia from "../ImageFromStrapiMedia/ImageFromStrapiMedia";

// Constants for marker sizes
const MARKER_SIZE_SELECTED = 50;
const MARKER_SIZE_DEFAULT = 40;
const FALLBACK_MARKER_URL = "/images/map-marker.png";

// Cache for image data URLs to avoid fetching the same image multiple times
const imageCache = new Map<string, string>();

// Types
interface MapMarkerProps {
  point: Point | null;
  selectedPointId?: string | number;
  onMarkerPress?: (id: string | number) => void;
  mapRef: React.RefObject<any>;
  setSelectedPointId?: (id: string | number) => void;
  clusterer?: any;
}

// Function to fetch image and convert to data URL with caching
const fetchImageAsDataUrl = async (url: string): Promise<string> => {
  if (imageCache.has(url)) {
    return imageCache.get(url) || "";
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    imageCache.set(url, dataUrl);
    return dataUrl;
  } catch (error) {
    console.error("Failed to fetch image:", error);
    // Fallback to original URL
    return url;
  }
};

// Helper function to generate SVG for custom marker
const generateCustomMarkerSvg = (
  size: number,
  bgColor: string,
  borderColor: string,
  pointerColor: string,
  imageDataUrl: string
) => `
  <svg width="${size}" height="${size + 10}" viewBox="0 0 ${size} ${
  size + 10
}" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="${size - 6}" height="${
  size - 6
}" rx="6" ry="6" fill="rgba(0,0,0,0.3)" />
    <rect x="2" y="2" width="${size - 4}" height="${
  size - 4
}" rx="6" ry="6" fill="${bgColor}" />
    <line x1="2" y1="2" x2="${
      size - 2
    }" y2="2" stroke="${borderColor}" stroke-width="${
  borderColor === "transparent" ? 0 : 2
}" />
    <line x1="2" y1="2" x2="2" y2="${
      size - 2
    }" stroke="${borderColor}" stroke-width="${
  borderColor === "transparent" ? 0 : 2
}" />
    <line x1="${size - 2}" y1="2" x2="${size - 2}" y2="${
  size - 2
}" stroke="${borderColor}" stroke-width="${
  borderColor === "transparent" ? 0 : 2
}" />
    <line x1="2" y1="${size - 2}" x2="${size / 2 - 8}" y2="${
  size - 2
}" stroke="${borderColor}" stroke-width="${
  borderColor === "transparent" ? 0 : 2
}" />
    <line x1="${size / 2 + 8}" y1="${size - 2}" x2="${size - 2}" y2="${
  size - 2
}" stroke="${borderColor}" stroke-width="${
  borderColor === "transparent" ? 0 : 2
}" />
    <polygon points="${size / 2 - 8},${size - 2} ${size / 2},${size + 10} ${
  size / 2 + 8
},${size - 2}" fill="${pointerColor}" />
    <line x1="${size / 2 - 8}" y1="${size - 2}" x2="${size / 2}" y2="${
  size + 10
}" stroke="${borderColor}" stroke-width="${
  borderColor === "transparent" ? 0 : 2
}" />
    <line x1="${size / 2 + 8}" y1="${size - 2}" x2="${size / 2}" y2="${
  size + 10
}" stroke="${borderColor}" stroke-width="${
  borderColor === "transparent" ? 0 : 2
}" />
    <image href="${imageDataUrl}" x="${size / 2 - size * 0.35}" y="${
  size / 2 - size * 0.35
}" width="${size * 0.7}" height="${size * 0.7}" />
  </svg>
`;

const generateCustomMarkerIcon = async (
  threadType: ThreadType | undefined,
  isSelected: boolean
) => {
  if (!threadType) {
    return {
      url: FALLBACK_MARKER_URL,
      scaledSize: isSelected
        ? new google.maps.Size(MARKER_SIZE_SELECTED, MARKER_SIZE_SELECTED)
        : new google.maps.Size(MARKER_SIZE_DEFAULT, MARKER_SIZE_DEFAULT),
    };
  }

  const size = isSelected ? MARKER_SIZE_SELECTED : MARKER_SIZE_DEFAULT;
  const markerColor = threadType.mainColor || "#000000";
  const selectedImage = isSelected
    ? threadType.imageLightMode
    : threadType.imageDarkMode;
  const markerTypeImageUrl = ImageFromStrapiMedia(selectedImage)?.uri;

  if (!selectedImage?.url) {
    return {
      url: FALLBACK_MARKER_URL,
      scaledSize: isSelected
        ? new google.maps.Size(MARKER_SIZE_SELECTED, MARKER_SIZE_SELECTED)
        : new google.maps.Size(MARKER_SIZE_DEFAULT, MARKER_SIZE_DEFAULT),
    };
  }

  const bgColor = isSelected ? "white" : markerColor;
  const borderColor = isSelected ? markerColor : "transparent";
  const pointerColor = bgColor;

  try {
    // Fetch the image and convert to data URL
    const imageDataUrl = await fetchImageAsDataUrl(markerTypeImageUrl);

    const svg = generateCustomMarkerSvg(
      size,
      bgColor,
      borderColor,
      pointerColor,
      imageDataUrl
    );
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const objectURL = URL.createObjectURL(blob);

    return {
      url: objectURL,
      scaledSize: new google.maps.Size(size, size + 10),
      anchor: new google.maps.Point(size / 2, size + 10), // Anchor at the bottom point
    };
  } catch (error) {
    console.error("Failed to generate custom marker icon:", error);
    return {
      url: FALLBACK_MARKER_URL,
      scaledSize: isSelected
        ? new google.maps.Size(MARKER_SIZE_SELECTED, MARKER_SIZE_SELECTED)
        : new google.maps.Size(MARKER_SIZE_DEFAULT, MARKER_SIZE_DEFAULT),
    };
  }
};

function MapMarker({
  point,
  selectedPointId,
  onMarkerPress,
  mapRef,
  setSelectedPointId,
  clusterer,
}: MapMarkerProps) {
  const position = useMemo(
    () => ({
      lat: point?.lat ?? 0,
      lng: point?.lng ?? 0,
    }),
    [point?.lat, point?.lng]
  );

  const isSelected = point?.id === selectedPointId;

  const [icon, setIcon] = useState<any>(null);

  useEffect(() => {
    if (!point) {
      setIcon(null);
      return;
    }
    generateCustomMarkerIcon(point.type, isSelected).then(setIcon);
  }, [point?.type, isSelected, point]);

  if (!point) return null;

  return (
    <Marker
      key={`${point.id}-${isSelected}`}
      position={position}
      onClick={() => {
        if (mapRef?.current) {
          mapRef.current.panTo(position);
        }
        onMarkerPress?.(point.id);
        setSelectedPointId?.(point.id);
      }}
      icon={icon}
      zIndex={isSelected ? 12 : 11}
      title={point.name ?? ""}
      clusterer={clusterer}
    />
  );
}

export default MapMarker;
